import React, { useState, useEffect } from 'react'
import { Select } from './Select'
import { apiClient } from '@/services/api'

interface UnitDefinition {
  type: string
  category: string
  name: string
  symbol: string
  baseConversion: number
  precision: number
}

interface UnitsResponse {
  definitions: Record<string, UnitDefinition>
  grouped: Record<string, UnitDefinition[]>
}

interface UnitSelectorProps {
  value: string
  onChange: (value: string) => void
  category?: string // Filter by specific category
  recommendedUnits?: string[] // Show recommended units first
  className?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
}

export const UnitSelector: React.FC<UnitSelectorProps> = ({
  value,
  onChange,
  category,
  recommendedUnits,
  className,
  placeholder = "Select unit...",
  required = false,
  disabled = false
}) => {
  const [availableUnits, setAvailableUnits] = useState<UnitsResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUnits()
  }, [])

  const fetchUnits = async () => {
    try {
      const response = await apiClient.get('/materials/units')
      setAvailableUnits(response)
    } catch (error) {
      console.error('Error fetching units:', error)
    } finally {
      setLoading(false)
    }
  }

  const getFilteredUnits = () => {
    if (!availableUnits) return {}
    
    if (category) {
      // Filter by specific category
      return { [category]: availableUnits.grouped[category] || [] }
    }
    
    return availableUnits.grouped
  }

  const getRecommendedUnits = () => {
    if (!recommendedUnits || !availableUnits) return []
    
    return recommendedUnits
      .map(unitType => availableUnits.definitions[unitType])
      .filter(Boolean)
  }

  const filteredUnits = getFilteredUnits()
  const recommended = getRecommendedUnits()

  if (loading) {
    return (
      <Select value="" onChange={() => {}} disabled className={className}>
        <option value="">Loading units...</option>
      </Select>
    )
  }

  return (
    <Select
      value={value}
      onValueChange={onChange}
      className={className}
      required={required}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      
      {/* Show recommended units first if provided */}
      {recommended.length > 0 && (
        <optgroup label="Recommended">
          {recommended.map(unit => (
            <option key={unit.type} value={unit.type}>
              {unit.name} ({unit.symbol})
            </option>
          ))}
        </optgroup>
      )}
      
      {/* Show all units grouped by category */}
      {Object.entries(filteredUnits).map(([categoryName, units]) => (
        <optgroup key={categoryName} label={categoryName}>
          {units.map(unit => (
            <option key={unit.type} value={unit.type}>
              {unit.name} ({unit.symbol})
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  )
}

// Helper hook for getting unit information
export const useUnitInfo = (unitType: string) => {
  const [unitInfo, setUnitInfo] = useState<UnitDefinition | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!unitType) {
      setUnitInfo(null)
      setLoading(false)
      return
    }

    const fetchUnitInfo = async () => {
      try {
        const response = await apiClient.get('/materials/units')
        const unitDef = response.definitions[unitType]
        setUnitInfo(unitDef || null)
      } catch (error) {
        console.error('Error fetching unit info:', error)
        setUnitInfo(null)
      } finally {
        setLoading(false)
      }
    }

    fetchUnitInfo()
  }, [unitType])

  return { unitInfo, loading }
}

// Helper function to format values with units
export const formatValueWithUnit = (value: number, unitType: string, unitInfo?: UnitDefinition) => {
  if (!unitInfo) return `${value} ${unitType}`
  
  const roundedValue = Math.round(value * Math.pow(10, unitInfo.precision)) / Math.pow(10, unitInfo.precision)
  return `${roundedValue} ${unitInfo.symbol}`
}